import type { HoldingsImportErrorDetails, HoldingsImportResult } from '@fruuit/shared'
import {
  HOLDINGS_IMPORT_MAX_BYTES,
  HOLDINGS_IMPORT_MAX_ROWS,
  HOLDINGS_IMPORT_OPTIONAL_HEADERS,
  HOLDINGS_IMPORT_REQUIRED_HEADERS,
} from '@fruuit/shared/contracts/holdings'
import { Button, Callout, Dialog, DialogCancel, useToast } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type ChangeEvent, useRef, useState } from 'react'
import { ApiError, errorMessage, newRequestId } from '../../api/client'
import { plural } from '../coverage/format'
import { uploadHoldingsCsv } from './api'

/**
 * Holdings CSV import (P-REC-003, IO-001..014). Validate first (a dry run),
 * then import; the server is all-or-nothing and lists every row error.
 */
export function ImportDialog({
  open,
  onClose,
  org,
}: {
  open: boolean
  onClose: () => void
  org: string | null
}) {
  const [file, setFile] = useState<{ name: string; text: string; size: number } | null>(null)
  const [busy, setBusy] = useState<'validate' | 'import' | null>(null)
  const [result, setResult] = useState<HoldingsImportResult | null>(null)
  const [error, setError] = useState<ApiError | Error | null>(null)
  const requestId = useRef<string | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  function reset() {
    setFile(null)
    setResult(null)
    setError(null)
    requestId.current = null
  }

  function close() {
    if (busy) return
    reset()
    onClose()
  }

  async function choose(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0]
    setResult(null)
    setError(null)
    requestId.current = null
    if (!chosen) return setFile(null)
    if (chosen.size > HOLDINGS_IMPORT_MAX_BYTES) {
      setFile(null)
      setError(new Error('The file is larger than 2 MB. Split it and import each part.'))
      return
    }
    setFile({ name: chosen.name, text: await chosen.text(), size: chosen.size })
  }

  async function run(dryRun: boolean) {
    if (!file || busy) return
    setBusy(dryRun ? 'validate' : 'import')
    setError(null)
    // A retry of the same import reuses its request id, so the server replays (D-14).
    if (!dryRun) requestId.current ??= newRequestId()
    try {
      const outcome = await uploadHoldingsCsv(file, {
        org,
        dryRun,
        ...(dryRun ? {} : { requestId: requestId.current as string }),
      })
      setResult(outcome)
      if (!dryRun) {
        await Promise.all(
          ['holdings', 'coverage', 'portfolios', 'mapping-exceptions', 'dashboard'].map((key) =>
            queryClient.invalidateQueries({ queryKey: [key] }),
          ),
        )
        toast({
          tone: 'success',
          message: `${plural(outcome.created_holdings, 'holding')} imported; ${plural(outcome.mapping_exceptions_created, 'mapping exception')} raised.`,
        })
      }
    } catch (caught) {
      setResult(null)
      setError(caught instanceof Error ? caught : new Error(String(caught)))
      if (!dryRun && caught instanceof ApiError && caught.status < 500) requestId.current = null
    } finally {
      setBusy(null)
    }
  }

  const details =
    error instanceof ApiError
      ? (error.details as HoldingsImportErrorDetails | undefined)
      : undefined
  const imported = result && !result.dry_run

  return (
    <Dialog
      open={open}
      onClose={close}
      size="lg"
      title="Import holdings"
      description="Upload a CSV of positions. Nothing is imported unless every row is valid."
      footer={
        imported ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <DialogCancel />
            <Button
              variant="secondary"
              disabled={!file}
              loading={busy === 'validate'}
              onClick={() => run(true)}
            >
              Validate
            </Button>
            <Button disabled={!file} loading={busy === 'import'} onClick={() => run(false)}>
              Import
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4 text-sm">
        <Callout>
          Columns: <code>{HOLDINGS_IMPORT_REQUIRED_HEADERS.join(', ')}</code>; optional{' '}
          <code>{HOLDINGS_IMPORT_OPTIONAL_HEADERS.join(', ')}</code>. Up to{' '}
          {HOLDINGS_IMPORT_MAX_ROWS.toLocaleString('en-GB')} rows and 2 MB. Identifiers that don’t
          map to a covered security are kept and raised as mapping exceptions.
        </Callout>
        <label className="block">
          <span className="mb-1.5 block font-semibold text-ink">CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={choose}
            disabled={Boolean(busy) || Boolean(imported)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-line-strong file:bg-surface file:px-3 file:py-2 file:font-semibold file:text-teal"
          />
        </label>
        {file ? (
          <p className="text-sage">
            {file.name} · {(file.size / 1024).toFixed(1)} KB
          </p>
        ) : null}
        {result ? (
          <Callout
            tone={result.dry_run ? 'info' : 'note'}
            role="status"
            title={result.dry_run ? 'The file is valid' : 'Import complete'}
          >
            {result.input_rows} rows: {result.dry_run ? 'would create' : 'created'}{' '}
            {plural(result.created_holdings, 'holding')}, {result.existing_holdings} already
            recorded, {result.mapped_rows} mapped, {result.unresolved_rows} unmapped
            {result.created_portfolios.length > 0
              ? `; new portfolios: ${result.created_portfolios.join(', ')}`
              : ''}
            .
          </Callout>
        ) : null}
        {error ? (
          <Callout
            tone="danger"
            role="alert"
            title={details ? 'Nothing was imported' : 'The file could not be imported'}
          >
            {details
              ? `Correct the ${plural((details as { error_count?: number }).error_count ?? details.errors.length, 'listed issue')} and upload again.`
              : errorMessage(error)}
          </Callout>
        ) : null}
        {details && details.errors.length > 0 ? (
          <div className="max-h-72 overflow-auto rounded-md border border-line">
            <table className="w-full text-left text-sm">
              <caption className="sr-only">Row errors</caption>
              <thead className="sticky top-0 bg-[#f5f8f3] text-xs text-sage uppercase">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    Row
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Column
                  </th>
                  <th scope="col" className="px-3 py-2">
                    Problem
                  </th>
                </tr>
              </thead>
              <tbody>
                {details.errors.map((e, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: server order, may repeat a row
                  <tr key={index} className="border-t border-line">
                    <td className="px-3 py-2 tabular-nums">{e.row || 'File'}</td>
                    <td className="px-3 py-2 text-sage">{e.column ?? '—'}</td>
                    <td className="px-3 py-2">{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {details.truncated ? (
              <p className="px-3 py-2 text-xs text-sage">
                More errors exist; the first {details.errors.length} are listed.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}
